import { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ChevronLeftIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  XMarkIcon,
  ArrowsRightLeftIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import PageHeader from "../../components/layout/PageHeader";
import { Input } from "../../components/ui/Input";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";
import { Modal } from "../../components/ui/Modal";
import { useDebounce } from "../../hooks/useDebounce";
import { useAuth } from "../../hooks/useAuth";
import {
  campersApi,
  type DuplicateCamperSummary,
  type DuplicateConfidence,
  type DuplicateSuggestion,
  type DuplicateSuggestionsResponse,
  type MergeResult,
} from "../../api/campers";
import CamperMergeForm from "../../components/forms/CamperMergeForm";
import type { Camper, Enrollment, PaginatedResponse } from "../../types";

function summaryToCamper(s: DuplicateCamperSummary): Camper {
  // Synthesize a minimal Camper for the merge picker. The wizard re-fetches
  // detailed data via the merge preview endpoint when the dialog opens, so
  // we only need enough here to render the picker tiles.
  const enrollments: Enrollment[] = Array.from({ length: s.enrollmentCount }, () =>
    ({}) as Enrollment
  );
  return {
    id: s.id,
    firstName: s.firstName,
    lastName: s.lastName,
    dateOfBirth: s.dateOfBirth,
    campMinderPersonId: s.campMinderPersonId,
    archivedAt: s.archivedAt,
    completionCount: s.completionCount,
    honorCount: 0,
    enrollments,
  };
}

const PAGE_SIZE = 25;

function uniqueYears(camper: Camper): number[] {
  return Array.from(
    new Set((camper.enrollments ?? []).map((e) => e.year))
  ).sort((a, b) => b - a);
}

function formatYears(camper: Camper): string {
  const years = uniqueYears(camper);
  if (years.length === 0) return "—";
  if (years.length <= 4) return years.join(", ");
  return `${years.slice(0, 3).join(", ")}, +${years.length - 3} more`;
}

function enrollmentCount(camper: Camper): number {
  return camper.enrollments?.length ?? 0;
}

function formatDob(dob?: string | null): string {
  if (!dob) return "—";
  // Stored as YYYY-MM-DD; render compact
  return dob;
}

export default function ManageCampers() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [page, setPage] = useState(1);

  const [data, setData] = useState<PaginatedResponse<Camper> | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [winnerId, setWinnerId] = useState<number | null>(null);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeResult, setMergeResult] = useState<MergeResult | null>(null);
  // Campers supplied directly to the merge dialog (e.g. from a suggestion row
  // whose campers may not be in the current search results).
  const [overrideMergeCampers, setOverrideMergeCampers] = useState<Camper[] | null>(null);

  // Suggested-merges section state
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<DuplicateSuggestionsResponse | null>(null);
  const [showDismissed, setShowDismissed] = useState(false);

  const debouncedFirst = useDebounce(firstName, 300);
  const debouncedLast = useDebounce(lastName, 300);
  const debouncedDob = useDebounce(dateOfBirth, 300);

  const hasFilter = !!debouncedFirst || !!debouncedLast || !!debouncedDob;

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [debouncedFirst, debouncedLast, debouncedDob]);

  useEffect(() => {
    let cancelled = false;
    if (!hasFilter) {
      setData(null);
      return;
    }
    setLoading(true);
    campersApi
      .list({
        page,
        pageSize: PAGE_SIZE,
        firstName: debouncedFirst || undefined,
        lastName: debouncedLast || undefined,
        dateOfBirth: debouncedDob || undefined,
        includeArchived: "true",
      })
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch(() => {
        if (!cancelled) toast.error("Failed to search campers");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedFirst, debouncedLast, debouncedDob, page, hasFilter, refreshKey]);

  function clearFilters() {
    setFirstName("");
    setLastName("");
    setDateOfBirth("");
  }

  const loadSuggestions = useCallback(
    async (opts: { includeDismissed?: boolean } = {}) => {
      if (!isAdmin) return;
      setSuggestionsLoading(true);
      try {
        const res = await campersApi.duplicates({
          includeDismissed: opts.includeDismissed ?? showDismissed,
        });
        setSuggestions(res);
        // Auto-expand once on first load if there are high-confidence pairs.
        if (res.counts.high > 0) setSuggestionsOpen(true);
      } catch (err: unknown) {
        const msg =
          (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
        // Counselors/directors hit 403 — quietly skip; the section is gated by isAdmin anyway.
        if (msg) toast.error(msg);
      } finally {
        setSuggestionsLoading(false);
      }
    },
    [isAdmin, showDismissed]
  );

  useEffect(() => {
    loadSuggestions({ includeDismissed: showDismissed });
  }, [loadSuggestions, refreshKey, showDismissed]);

  function openMergeFromSuggestion(s: DuplicateSuggestion) {
    const a = summaryToCamper(s.camperA);
    const b = summaryToCamper(s.camperB);
    setSelectedIds([a.id, b.id]);
    // A CampMinder orphan must default to the merge loser — its records flow
    // onto the surviving live camper. The admin can still swap in the wizard.
    const aOrphan = s.camperA.cmOrphanedAt != null;
    const bOrphan = s.camperB.cmOrphanedAt != null;
    const preferredWinnerId =
      aOrphan !== bOrphan ? (aOrphan ? b.id : a.id) : undefined;
    openMergeDialog([a, b], preferredWinnerId);
  }

  async function handleDismiss(s: DuplicateSuggestion) {
    try {
      await campersApi.dismissDuplicate(s.camperA.id, s.camperB.id);
      toast.success("Dismissed.");
      // Optimistic local update — drop this pair from the list.
      setSuggestions((prev) =>
        prev
          ? {
              counts: {
                ...prev.counts,
                [s.confidence]: Math.max(0, prev.counts[s.confidence] - 1),
              },
              suggestions: prev.suggestions.filter(
                (x) =>
                  !(x.camperA.id === s.camperA.id && x.camperB.id === s.camperB.id)
              ),
            }
          : prev
      );
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || "Failed to dismiss");
    }
  }

  async function handleRestore(s: DuplicateSuggestion) {
    if (s.dismissalId == null) return;
    try {
      await campersApi.undismissDuplicate(s.dismissalId);
      toast.success("Restored.");
      await loadSuggestions({ includeDismissed: showDismissed });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || "Failed to restore");
    }
  }

  function toggleSelect(id: number) {
    setSelectedIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((x) => x !== id);
      }
      if (prev.length >= 2) {
        toast("Only 2 campers can be merged at a time.");
        return prev;
      }
      return [...prev, id];
    });
  }

  const selectedCampers = useMemo(() => {
    // Override takes precedence — used when the merge dialog is opened from
    // a suggestion row whose campers might not be in the current search.
    if (overrideMergeCampers && overrideMergeCampers.length === 2) {
      return overrideMergeCampers;
    }
    if (!data) return [];
    return selectedIds
      .map((id) => data.data.find((c) => c.id === id))
      .filter((c): c is Camper => !!c);
  }, [data, selectedIds, overrideMergeCampers]);

  function openMergeDialog(campers?: Camper[], preferredWinnerId?: number) {
    const pair = campers ?? selectedCampers;
    if (pair.length !== 2) return;
    if (campers) setOverrideMergeCampers(campers);
    // Default winner: the richer record (more ACs + enrollments). Ties fall
    // back to first-supplied. Admin can swap inside the wizard.
    const [a, b] = pair;
    const score = (c: Camper) => (c.completionCount ?? 0) + enrollmentCount(c);
    setWinnerId(preferredWinnerId ?? (score(a) >= score(b) ? a.id : b.id));
    setMergeResult(null);
    setMergeOpen(true);
  }

  function handleMergeSuccess(result: MergeResult) {
    setMergeResult(result);
    setSelectedIds([]);
    setRefreshKey((k) => k + 1);
  }

  function closeMergeDialog() {
    setMergeOpen(false);
    setMergeResult(null);
    setWinnerId(null);
    setOverrideMergeCampers(null);
  }

  return (
    <div>
      <PageHeader
        title="Manage Campers"
        subtitle="Search across all years and merge duplicate camper records"
      />

      <div className="p-6 lg:p-8 space-y-5">
        <Link
          to="/admin"
          className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-800"
        >
          <ChevronLeftIcon className="w-4 h-4" />
          Back to Admin
        </Link>

        {isAdmin && (
          <SuggestionsSection
            open={suggestionsOpen}
            setOpen={setSuggestionsOpen}
            loading={suggestionsLoading}
            suggestions={suggestions}
            showDismissed={showDismissed}
            setShowDismissed={setShowDismissed}
            onReviewMerge={openMergeFromSuggestion}
            onDismiss={handleDismiss}
            onRestore={handleRestore}
          />
        )}

        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          <Input
            label="First name"
            placeholder="e.g. Mike"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          <Input
            label="Last name"
            placeholder="e.g. Pinney"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
          <Input
            label="Date of birth"
            type="date"
            value={dateOfBirth}
            onChange={(e) => setDateOfBirth(e.target.value)}
          />
          <div className="flex items-center gap-3">
            {hasFilter && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-700 transition-colors"
              >
                <XMarkIcon className="w-4 h-4" />
                Clear
              </button>
            )}
            {data && (
              <span className="text-xs text-stone-400">
                {data.total} match{data.total !== 1 ? "es" : ""}
              </span>
            )}
          </div>
        </div>

        {/* Selection bar */}
        {selectedIds.length > 0 && (
          <div className="flex items-center justify-between rounded-xl bg-rose-50 border border-rose-200 px-4 py-3">
            <div className="text-sm text-rose-900">
              <span className="font-semibold">{selectedIds.length}</span> selected
              {selectedIds.length === 1 && " — pick one more to merge"}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSelectedIds([])}
                className="text-sm text-rose-700 hover:text-rose-900"
              >
                Clear
              </button>
              {isAdmin && (
                <Button
                  variant="danger"
                  size="sm"
                  icon={<ArrowsRightLeftIcon className="w-4 h-4" />}
                  disabled={selectedIds.length !== 2}
                  onClick={() => openMergeDialog()}
                >
                  Merge
                </Button>
              )}
            </div>
          </div>
        )}
        {!isAdmin && selectedIds.length === 2 && (
          <p className="text-xs text-amber-700 -mt-2">
            Only an admin role can merge campers.
          </p>
        )}

        {/* Results table */}
        {!hasFilter ? (
          <div className="rounded-xl border border-dashed border-stone-300 bg-stone-50 p-10 text-center text-sm text-stone-500">
            Enter a first name, last name, or date of birth above to find campers.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-stone-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-stone-50 border-b border-stone-200">
                  <th className="px-4 py-3 w-10" />
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wide">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wide">
                    DOB
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wide">
                    Years
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wide">
                    CampMinder
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-stone-500 uppercase tracking-wide">
                    Enrollments
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-stone-500 uppercase tracking-wide">
                    ACs
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-stone-500 uppercase tracking-wide">
                    Honors
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wide">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100 bg-white">
                {loading ? (
                  <tr>
                    <td colSpan={9} className="py-16 text-center text-stone-400">
                      <div className="flex items-center justify-center gap-2">
                        <span className="w-5 h-5 border-2 border-forest-500 border-t-transparent rounded-full animate-spin" />
                        Searching…
                      </div>
                    </td>
                  </tr>
                ) : data && data.data.length > 0 ? (
                  data.data.map((c) => {
                    const checked = selectedIds.includes(c.id);
                    return (
                      <tr
                        key={c.id}
                        className={`transition-colors ${
                          checked ? "bg-rose-50/60" : "hover:bg-stone-50"
                        }`}
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleSelect(c.id)}
                            className="w-4 h-4 rounded border-stone-300 text-forest-600 focus:ring-forest-500"
                            aria-label={`Select ${c.firstName} ${c.lastName}`}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => navigate(`/campers/${c.id}`)}
                            className="font-medium text-stone-900 hover:text-forest-700 hover:underline"
                          >
                            {c.firstName} {c.lastName}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-stone-600 tabular-nums">
                          {formatDob(c.dateOfBirth)}
                        </td>
                        <td className="px-4 py-3 text-stone-600 tabular-nums">
                          {formatYears(c)}
                        </td>
                        <td className="px-4 py-3 text-stone-500 tabular-nums">
                          {c.campMinderPersonId ?? "—"}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums font-medium text-stone-700">
                          {enrollmentCount(c)}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums font-medium text-stone-700">
                          {c.completionCount ?? 0}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums font-medium text-amber-600">
                          {c.honorCount ?? 0}
                        </td>
                        <td className="px-4 py-3">
                          {c.archivedAt ? (
                            <Badge label="Archived" color="stone" />
                          ) : (
                            <Badge label="Active" color="green" />
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={9} className="py-12 text-center text-stone-400 text-sm">
                      No campers match those filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {data && data.total > data.pageSize && (
          <div className="flex items-center justify-between text-sm text-stone-500">
            <span>
              Showing {(page - 1) * data.pageSize + 1}–
              {Math.min(page * data.pageSize, data.total)} of {data.total}
            </span>
            <div className="flex gap-2">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1.5 rounded-lg border border-stone-200 hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <button
                disabled={page * data.pageSize >= data.total}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 rounded-lg border border-stone-200 hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Merge dialog */}
      <Modal open={mergeOpen} onClose={closeMergeDialog} title="Merge campers" size="lg">
        {mergeResult ? (
          <MergeResultView result={mergeResult} onClose={closeMergeDialog} />
        ) : (
          (() => {
            if (selectedCampers.length !== 2 || winnerId == null) {
              return (
                <p className="text-sm text-stone-500">Select exactly two campers to merge.</p>
              );
            }
            const winner = selectedCampers.find((c) => c.id === winnerId);
            const loser = selectedCampers.find((c) => c.id !== winnerId);
            if (!winner || !loser) {
              return (
                <p className="text-sm text-stone-500">Select exactly two campers to merge.</p>
              );
            }
            return (
              <CamperMergeForm
                loser={loser}
                winner={winner}
                onCancel={closeMergeDialog}
                onSuccess={handleMergeSuccess}
              />
            );
          })()
        )}
      </Modal>
    </div>
  );
}

// ─── Merge stat tile ──────────────────────────────────────────────────────────

function Stat({
  label,
  value,
  highlight,
}: {
  label: string;
  value: number;
  highlight: boolean;
}) {
  return (
    <div
      className={`rounded-lg border px-2 py-1.5 text-center ${
        highlight
          ? "border-forest-300 bg-white"
          : "border-stone-200 bg-stone-50"
      }`}
    >
      <div
        className={`text-lg font-semibold tabular-nums ${
          highlight ? "text-forest-700" : "text-stone-700"
        }`}
      >
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-wide text-stone-500">
        {label}
      </div>
    </div>
  );
}


// ─── Merge result ─────────────────────────────────────────────────────────────

function MergeResultView({
  result,
  onClose,
}: {
  result: MergeResult;
  onClose: () => void;
}) {
  const { moved, conflicts } = result;
  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-forest-50 border border-forest-200 p-4">
        <p className="text-sm font-semibold text-forest-900 mb-2">Merge complete</p>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-forest-900">
          <dt className="text-forest-700">Completions moved</dt>
          <dd className="tabular-nums">{moved.completions}</dd>
          <dt className="text-forest-700">Enrollments moved</dt>
          <dd className="tabular-nums">{moved.enrollments}</dd>
          <dt className="text-forest-700">Honors moved</dt>
          <dd className="tabular-nums">{moved.honors}</dd>
          <dt className="text-forest-700">Assessments moved</dt>
          <dd className="tabular-nums">{moved.assessments}</dd>
          <dt className="text-forest-700">Linked users updated</dt>
          <dd className="tabular-nums">{moved.users}</dd>
        </dl>
      </div>

      {conflicts.length > 0 && (
        <div className="rounded-lg border border-stone-200 p-4">
          <p className="text-sm font-semibold text-stone-800 mb-2">
            {conflicts.length} conflict{conflicts.length !== 1 ? "s" : ""} resolved
          </p>
          <ul className="text-xs text-stone-600 space-y-1 max-h-40 overflow-y-auto">
            {conflicts.map((c, i) => (
              <li key={i} className="font-mono">
                {c.table}
                {c.achievement_credit_id != null && ` ac=${c.achievement_credit_id}`}
                {c.year != null && ` year=${c.year}`}
                {c.term_id != null && ` term=${c.term_id}`}
                {" → "}
                {c.resolution} (dropped row #{c.dropped_row_id})
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex justify-end pt-2">
        <Button variant="primary" onClick={onClose}>
          Done
        </Button>
      </div>
    </div>
  );
}

// ─── Suggestions section ─────────────────────────────────────────────────────

const CONFIDENCE_COPY: Record<DuplicateConfidence, { label: string; tone: "danger" | "warning" | "neutral" }> = {
  high: { label: "High", tone: "danger" },
  medium: { label: "Medium", tone: "warning" },
  low: { label: "Low", tone: "neutral" },
};

interface SuggestionsSectionProps {
  open: boolean;
  setOpen: (v: boolean) => void;
  loading: boolean;
  suggestions: DuplicateSuggestionsResponse | null;
  showDismissed: boolean;
  setShowDismissed: (v: boolean) => void;
  onReviewMerge: (s: DuplicateSuggestion) => void;
  onDismiss: (s: DuplicateSuggestion) => void;
  onRestore: (s: DuplicateSuggestion) => void;
}

function SuggestionsSection({
  open,
  setOpen,
  loading,
  suggestions,
  showDismissed,
  setShowDismissed,
  onReviewMerge,
  onDismiss,
  onRestore,
}: SuggestionsSectionProps) {
  const counts = suggestions?.counts ?? { high: 0, medium: 0, low: 0 };
  const total = counts.high + counts.medium + counts.low;
  const list = suggestions?.suggestions ?? [];
  const active = list.filter((s) => !s.dismissedAt);
  const dismissed = list.filter((s) => s.dismissedAt);

  const groups: { label: string; confidence: DuplicateConfidence; items: DuplicateSuggestion[] }[] = [
    { label: "High confidence", confidence: "high", items: active.filter((s) => s.confidence === "high") },
    { label: "Medium confidence", confidence: "medium", items: active.filter((s) => s.confidence === "medium") },
    { label: "Low confidence", confidence: "low", items: active.filter((s) => s.confidence === "low") },
  ];

  return (
    <div className="rounded-xl border border-stone-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-stone-50 rounded-xl transition-colors"
      >
        <div className="flex items-center gap-2">
          {open ? (
            <ChevronDownIcon className="w-4 h-4 text-stone-500" />
          ) : (
            <ChevronRightIcon className="w-4 h-4 text-stone-500" />
          )}
          <span className="text-sm font-semibold text-stone-800">
            Suggested merges
          </span>
          <span className="text-xs text-stone-500">
            {loading
              ? "Scanning…"
              : total === 0
              ? "No likely duplicates found."
              : `${counts.high} high · ${counts.medium} medium · ${counts.low} low`}
          </span>
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 pt-0 space-y-4 border-t border-stone-100">
          <div className="flex items-center justify-end pt-3">
            <label className="flex items-center gap-2 text-xs text-stone-600">
              <input
                type="checkbox"
                checked={showDismissed}
                onChange={(e) => setShowDismissed(e.target.checked)}
              />
              Show dismissed
            </label>
          </div>

          {loading && total === 0 && (
            <p className="text-sm text-stone-400">Scanning campers…</p>
          )}

          {!loading && total === 0 && !showDismissed && (
            <p className="text-sm text-stone-500">
              No likely duplicates found. We re-scan after every merge.
            </p>
          )}

          {groups.map((g) =>
            g.items.length === 0 ? null : (
              <div key={g.confidence}>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-2">
                  {g.label} ({g.items.length})
                </h4>
                <ul className="space-y-2">
                  {g.items.map((s) => (
                    <SuggestionRow
                      key={`${s.camperA.id}-${s.camperB.id}`}
                      suggestion={s}
                      onReviewMerge={() => onReviewMerge(s)}
                      onDismiss={() => onDismiss(s)}
                    />
                  ))}
                </ul>
              </div>
            )
          )}

          {showDismissed && dismissed.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-2">
                Dismissed ({dismissed.length})
              </h4>
              <ul className="space-y-2">
                {dismissed.map((s) => (
                  <SuggestionRow
                    key={`d-${s.camperA.id}-${s.camperB.id}`}
                    suggestion={s}
                    onReviewMerge={() => onReviewMerge(s)}
                    onRestore={() => onRestore(s)}
                  />
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SuggestionRow({
  suggestion,
  onReviewMerge,
  onDismiss,
  onRestore,
}: {
  suggestion: DuplicateSuggestion;
  onReviewMerge: () => void;
  onDismiss?: () => void;
  onRestore?: () => void;
}) {
  const tone = CONFIDENCE_COPY[suggestion.confidence].tone;
  const toneClass =
    tone === "danger"
      ? "bg-rose-100 text-rose-800 border-rose-200"
      : tone === "warning"
      ? "bg-amber-100 text-amber-800 border-amber-200"
      : "bg-stone-100 text-stone-700 border-stone-200";

  return (
    <li className="rounded-lg border border-stone-200 px-3 py-3 bg-white">
      <div className="flex flex-wrap items-start gap-3">
        <span
          className={`shrink-0 inline-flex items-center text-[10px] uppercase tracking-wide font-semibold border rounded-full px-2 py-0.5 ${toneClass}`}
        >
          {CONFIDENCE_COPY[suggestion.confidence].label}
        </span>
        <div className="flex-1 min-w-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <SuggestionCamperCard summary={suggestion.camperA} />
            <SuggestionCamperCard summary={suggestion.camperB} />
          </div>
          <p className="text-xs text-stone-600 mt-2">{suggestion.reason}</p>
          {suggestion.dismissedAt && (
            <p className="text-[11px] text-stone-400 mt-0.5">
              Dismissed
              {suggestion.dismissedByName && ` by ${suggestion.dismissedByName}`}
              {" on "}
              {new Date(suggestion.dismissedAt).toLocaleDateString()}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button size="sm" variant="primary" onClick={onReviewMerge}>
            Review merge
          </Button>
          {onDismiss && (
            <Button size="sm" variant="ghost" onClick={onDismiss}>
              Dismiss
            </Button>
          )}
          {onRestore && (
            <Button size="sm" variant="ghost" onClick={onRestore}>
              Restore
            </Button>
          )}
        </div>
      </div>
    </li>
  );
}

function SuggestionCamperCard({ summary }: { summary: DuplicateCamperSummary }) {
  return (
    <div className="rounded-md border border-stone-100 bg-stone-50 px-2.5 py-2">
      <p className="text-sm font-medium text-stone-800 truncate">
        {summary.firstName} {summary.lastName}
        {summary.cmOrphanedAt && (
          <span className="ml-1.5 inline-flex items-center text-[10px] uppercase tracking-wide font-semibold border rounded-full px-1.5 py-0.5 bg-amber-100 text-amber-800 border-amber-200">
            Orphaned in CampMinder
          </span>
        )}
        {summary.archivedAt && !summary.cmOrphanedAt && (
          <span className="ml-1.5 text-[10px] uppercase tracking-wide text-stone-400">
            archived
          </span>
        )}
      </p>
      <p className="text-[11px] text-stone-500">
        {summary.dateOfBirth ?? "no DOB"}
        {summary.campMinderPersonId != null && ` · CM #${summary.campMinderPersonId}`}
        {" · "}
        {summary.completionCount} ACs · {summary.enrollmentCount} enrollments
      </p>
    </div>
  );
}
